import { ref } from 'vue'

import { installedModules, moduleNavigation } from './registry'
import { buildGroups, mountedRule, requirementsMet } from './navigationRules'
import { mountedModules } from './instance'

// Sidebar entries owned by the shell itself. Modules add theirs through their
// frontend/index.js, and the two lists are merged here.
const SHELL_NAVIGATION = [
  { to: '/', labelKey: 'home.title', icon: 'home', group: 'main', exact: true },
  { to: '/notifications', labelKey: 'notifications.title', icon: 'bell', group: 'platform', order: 10 },
  // La documentación. Una entrada y no un icono suelto: quien la busca sin
  // venir de un «?» la busca en el menú, y el «?» de una pantalla es el atajo
  // para quien ya está delante de lo que no entiende.
  //
  // `newTab`: se abre en OTRA PESTAÑA, como el «?» de cada pantalla. La
  // documentación se lee con el ERP al lado; entrar en ella en la misma pestaña
  // es perder la pantalla en la que se estaba trabajando para leer cómo se hace.
  { to: '/help', labelKey: 'help.title', icon: 'plug', group: 'platform', order: 20, newTab: true },
  // Last of the platform block: the only entry here that is ours rather than
  // the client's, and the one nobody but us opens.
  { to: '/ops', labelKey: 'ops.title', permission: 'ops:read', icon: 'activity', group: 'platform', order: 90 },
]

export { GROUPS, buildGroups } from './navigationRules'

/**
 * Navigation the given user may actually see.
 *
 * The shell's own entries merged with whatever the installed modules
 * contribute. The rules — what a group is, how entries sort, how a permission
 * filters an entry and its children, which entry another module takes over —
 * are in navigationRules.js; this is only the wiring, and it is all this file is
 * allowed to be: it knows which modules exist, and nothing that knows that can
 * be reasoned about in a test.
 */
export function visibleNavigation(can) {
  return buildGroups([...SHELL_NAVIGATION, ...moduleNavigation], can, isInstalled, isMounted)
}

/**
 * The module that owns a path, or null for a screen the shell owns.
 *
 * Every navigation entry is already marked with where it came from (see
 * `moduleNavigation` in registry.js), so this is a lookup rather than a guess.
 * The longest match wins: `/quotes/templates` belongs to whatever registered
 * that entry, not to whatever registered `/quotes`, and the two can differ.
 *
 * It exists for the assistant. What the person is looking at is the cheapest
 * and most reliable hint about which module a question is about, and the
 * backend cannot work it out: it never sees a route.
 */
export function moduleForPath(path) {
  const entries = moduleNavigation.flatMap(entry => [
    entry,
    ...(entry.children ?? []).map(child => ({ ...child, module: entry.module })),
  ])

  return entries
    .filter(entry => path === entry.to || path.startsWith(`${entry.to}/`))
    .sort((a, b) => b.to.length - a.to.length)[0]?.module ?? null
}

/**
 * Si el módulo viene EN EL PAQUETE.
 *
 * El registro se construye de los módulos presentes en el repositorio al
 * compilar. Es la mitad de la pregunta, y la que sirve para la sustitución: si
 * `inventory` no viene en el paquete, no puede quedarse con el catálogo.
 */
function isInstalled(name) {
  return installedModules.includes(name)
}

const mounted = ref(null)
mountedModules().then((answer) => { mounted.value = answer })

/**
 * Y si además lo ha ARRANCADO esta instancia.
 *
 * La otra mitad, y la que faltaba. La imagen es la PLATAFORMA y el `client.yaml`
 * es la INSTANCIA —la misma imagen corre el ERP de un cliente, la demo pública y
 * una partida de ventas—, así que «está en la carpeta» dejó de significar «está
 * instalado». Lo dice `/health`, que ya publicaba la lista; ver la nota larga de
 * `instance.js`, que ya lo contaba para la documentación.
 *
 * Mientras no se sabe se contesta que sí: esconder media aplicación durante los
 * trescientos milisegundos que tarda `/health` es peor que enseñarla, y si el
 * servidor no contesta nunca, el menú se queda como estaba antes de esto.
 *
 * Es un `ref` y no una variable suelta para que el menú se rehaga solo cuando
 * llegue la respuesta: quien lo pinta lo lee dentro de un `computed`.
 */
function isMounted(name) {
  return mountedRule(isInstalled, mounted.value)(name)
}

/**
 * Lo mismo para el router: ¿se cumplen los `meta.requires` de esta ruta?
 *
 * Aquí sí se ESPERA a `/health`, al revés que el menú. El menú se pinta ya y se
 * corrige solo cuando llega la respuesta; una navegación no se puede corregir
 * después —se habría abierto una pantalla que pide datos a un módulo que no
 * está—. Es una petición por sesión (la promesa se comparte), así que la espera
 * solo la paga la primera ruta que lleva `requires`, y ninguna otra.
 *
 * Si `/health` no contesta, la regla responde como el menú: sí para lo que
 * viene en el paquete. Un corte de red no deja a nadie fuera de una pantalla.
 */
export async function routeRequirementsMet(requires) {
  if (!requires) return true
  const answer = await mountedModules()
  return requirementsMet(requires, mountedRule(isInstalled, answer))
}
